<template>
  <div>
    <ClinicPageHeader
      title="Profissionais de saúde"
      subtitle="Gestão operacional do corpo clínico"
    >
      <template #actions>
        <!-- Cadastrar profissional exige Profissionais = Editar (US-C11). -->
        <router-link
          v-if="canEdit('medicos')"
          :to="{ name: 'ClinicDoctorNew' }"
          class="btn btn--primary btn--sm"
        >
          + Adicionar profissional de saúde
        </router-link>
      </template>
    </ClinicPageHeader>

    <div class="dbody">
      <!-- Erro -->
      <ClinicStateBox
        v-if="state === 'error'"
        state="error"
        title="Não foi possível carregar os profissionais de saúde"
        message="Ocorreu um erro ao buscar o corpo clínico da clínica. Tente novamente."
      >
        <template #actions>
          <button class="btn btn--primary btn--sm" @click="reload">
            Tentar de novo
          </button>
        </template>
      </ClinicStateBox>

      <!-- Loading -->
      <template v-else-if="state === 'loading'">
        <div class="metrics4" style="margin-bottom:var(--space-5)">
          <div v-for="i in 4" :key="i" class="metric">
            <div class="skel-line" style="width:60%;margin-bottom:10px" />
            <div class="skel-line" style="width:40%;height:24px" />
          </div>
        </div>
        <div class="card card--flush">
          <div style="padding:var(--space-5)">
            <div
              v-for="i in 5"
              :key="i"
              class="skel-line"
              style="width:100%;height:20px;margin-bottom:16px"
            />
          </div>
        </div>
      </template>

      <!-- Conteúdo -->
      <template v-else>
        <!-- KPIs -->
        <div class="metrics4" style="margin-bottom:var(--space-5)">
          <ClinicKpi
            v-for="m in metrics"
            :key="m.label"
            :label="m.label"
            :value="m.value"
            :foot="m.foot"
            :trend="m.trend"
            :value-class="m.valueClass"
          />
        </div>

        <!-- Toolbar: busca + filtro de especialidade (refletidos na URL) -->
        <div class="dtoolbar">
          <div class="dsearch">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle
                cx="11"
                cy="11"
                r="7"
                stroke="currentColor"
                stroke-width="2"
              />
              <path
                d="M21 21l-4-4"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
            <input
              :value="filters.q"
              class="input"
              placeholder="Buscar profissional de saúde…"
              @input="onFilter('q', $event.target.value)"
            />
          </div>
          <select
            :value="filters.specialty"
            class="select"
            @change="onFilter('specialty', $event.target.value)"
          >
            <option value="">Todas as especialidades</option>
            <option v-for="s in specialties" :key="s.id" :value="s.name">
              {{ s.name }}
            </option>
          </select>
          <div style="flex:1" />
          <router-link
            v-if="canEdit('medicos')"
            :to="{ name: 'ClinicDoctorNew' }"
            class="btn btn--primary btn--sm"
          >
            + Adicionar profissional de saúde
          </router-link>
        </div>

        <!-- Tabela de médicos -->
        <div class="card card--flush">
          <table v-if="rows.length" class="table">
            <thead>
              <tr>
                <th>Profissional de saúde</th>
                <th>Especialidade</th>
                <th>Consultas/mês</th>
                <th>Ocupação</th>
                <th>Status</th>
                <th class="t-right">Ação</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="d in rows"
                :key="d.id"
                class="tr--link"
                @click="goProfile(d)"
              >
                <td>
                  <div class="row" style="gap:var(--space-3)">
                    <div class="avatar avatar--sm" :class="d.avatarClass">
                      <img
                        v-if="d.photo"
                        :src="d.photo"
                        :alt="d.name"
                        loading="lazy"
                        decoding="async"
                      />
                      <template v-else>{{ d.initials }}</template>
                    </div>
                    <div class="stack" style="gap:0">
                      <span class="cell-strong">{{ d.name }}</span>
                      <span
                        style="font-size:var(--font-size-xs);color:var(--color-gray-400)"
                      >
                        {{ d.crm }}
                      </span>
                    </div>
                  </div>
                </td>
                <td>{{ d.specialty }}</td>
                <td class="num">{{ d.appointmentsMonth }}</td>
                <td>
                  <div class="row" style="gap:var(--space-2)">
                    <div
                      style="flex:1;max-width:90px;height:7px;background:var(--color-gray-100);border-radius:var(--radius-full);overflow:hidden"
                    >
                      <i
                        style="display:block;height:100%"
                        :style="{
                          width: d.occupation + '%',
                          background: `var(--color-${occupationColor(
                            d.occupation
                          )})`
                        }"
                      />
                    </div>
                    <span
                      class="num"
                      style="font-size:var(--font-size-xs);color:var(--color-gray-500)"
                    >
                      {{ d.occupation }}%
                    </span>
                  </div>
                </td>
                <td>
                  <span
                    class="badge"
                    :class="`badge--${statusVariant(d.status)}`"
                  >
                    {{ d.statusLabel }}
                  </span>
                </td>
                <td class="t-right">
                  <button
                    class="btn btn--outline btn--sm"
                    @click.stop="goSchedule(d)"
                  >
                    Ver agenda
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <ClinicStateBox
            v-else
            state="empty"
            title="Nenhum profissional de saúde encontrado"
            message="Ajuste os filtros de busca ou adicione um novo profissional de saúde ao corpo clínico."
          />
        </div>

        <!-- Convites ainda sem aceite (pendentes e recusados) -->
        <div
          v-if="invitations.length"
          class="card card--flush"
          style="margin-top:var(--space-5)"
        >
          <div class="card-head flush">
            <h3>Convites</h3>
            <span class="badge badge--warning badge--no-dot">
              {{ invitations.length }}
            </span>
          </div>
          <table class="table">
            <tbody>
              <tr
                v-for="inv in invitations"
                :key="inv.id"
                data-test="invite-row"
              >
                <td>
                  <div class="row" style="gap:var(--space-3)">
                    <div class="avatar avatar--sm av-slate">
                      {{ inv.initials }}
                    </div>
                    <div class="stack" style="gap:0">
                      <span class="cell-strong">{{ inv.name }}</span>
                      <span
                        style="font-size:var(--font-size-xs);color:var(--color-gray-400)"
                      >
                        {{ inv.email }}
                      </span>
                    </div>
                  </div>
                </td>
                <td>
                  <span
                    class="badge"
                    :class="
                      inv.status === 'recusado'
                        ? 'badge--danger'
                        : 'badge--warning'
                    "
                  >
                    {{ inv.statusLabel }}
                  </span>
                </td>
                <td class="num">{{ formatDate(inv.createdAt) }}</td>
                <td class="t-right">
                  <div
                    v-if="canEdit('medicos')"
                    class="row"
                    style="gap:var(--space-1);justify-content:flex-end"
                  >
                    <button
                      class="btn btn--ghost btn--sm"
                      data-test="resend-invite"
                      :disabled="busyInviteId === inv.id"
                      @click="resendInvitation(inv)"
                    >
                      Reenviar convite
                    </button>
                    <button
                      class="btn btn--ghost btn--sm"
                      style="color:var(--color-danger)"
                      data-test="cancel-invite"
                      @click="cancelling = inv"
                    >
                      Cancelar convite
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
    <ClinicConfirmDialog
      v-if="cancelling"
      title="Cancelar convite"
      :message="
        `Cancelar o convite enviado para ${cancelling.email}? Ele some do painel do profissional de saúde.`
      "
      confirm-label="Cancelar convite"
      variant="danger"
      :loading="!!busyInviteId"
      @confirm="cancelInvitation"
      @close="cancelling = null"
    />
  </div>
</template>

<script>
import clinicCan from "@/mixins/clinicCan";
import { mapGetters } from "vuex";
import { ClinicDoctors, ClinicConfig } from "@/services/clinica";
import { formatMoney } from "@/data/clinica";
import clinicaListQuery from "@/mixins/clinicaListQuery";
import ClinicPageHeader from "@/components/clinica/ClinicPageHeader.vue";
import ClinicKpi from "@/components/clinica/ClinicKpi.vue";
import ClinicStateBox from "@/components/clinica/ClinicStateBox.vue";
import ClinicConfirmDialog from "@/components/clinica/ClinicConfirmDialog.vue";

export default {
  name: "ClinicDoctors",
  components: {
    ClinicPageHeader,
    ClinicKpi,
    ClinicStateBox,
    ClinicConfirmDialog
  },
  mixins: [clinicaListQuery, clinicCan],
  data() {
    return {
      // loading → ready/error. Preview de erro via ?state=error.
      state: "loading",
      // Especialidades reais (GET /clinica/specialties) — não fixture.
      specialties: [],
      // Filtros refletidos na URL (q, specialty, status).
      filters: { q: "", specialty: "", status: "" },
      rows: [],
      // Snapshot completo (sem filtro) da unidade → deriva os KPIs.
      allDoctors: [],
      // Convites de médico sem aceite (pendentes/recusados).
      invitations: [],
      busyInviteId: null,
      cancelling: null
    };
  },
  created() {
    this.filters = this.clinicaReadQuery({ q: "", specialty: "", status: "" });
    this.fetch();
    this.fetchSpecialties();
    this.fetchInvitations();
  },
  methods: {
    async fetchSpecialties() {
      try {
        const { data } = await ClinicConfig.specialties();
        this.specialties = data || [];
      } catch (e) {
        this.specialties = [];
      }
    },
    async fetch() {
      this.state = "loading";
      try {
        const fail = this.$route.query.state === "error";
        const unitId = this.selectedUnitId;
        const { data } = await ClinicDoctors.list({
          q: this.filters.q,
          specialty: this.filters.specialty,
          status: this.filters.status,
          unitId,
          fail
        });
        this.rows = data;
        // KPIs derivam do corpo clínico completo da unidade (sem filtro de busca).
        const all = await ClinicDoctors.list({ unitId });
        this.allDoctors = all.data;
        this.state = "ready";
      } catch (e) {
        this.state = "error";
      }
    },
    // Acessório: sem convites, a lista de médicos continua funcionando.
    async fetchInvitations() {
      try {
        const { data } = await ClinicDoctors.invitations();
        this.invitations = data || [];
      } catch (e) {
        this.invitations = [];
      }
    },
    async resendInvitation(inv) {
      this.busyInviteId = inv.id;
      try {
        await ClinicDoctors.resendInvitation(inv.id);
        // Reenviar um convite recusado o reabre na API: volta a "Convidado".
        Object.assign(inv, { status: "convidado", statusLabel: "Convidado" });
        this.$toast.success("Convite reenviado");
      } catch (e) {
        this.$toast.error("Não foi possível reenviar o convite");
      } finally {
        this.busyInviteId = null;
      }
    },
    async cancelInvitation() {
      const inv = this.cancelling;
      this.busyInviteId = inv.id;
      try {
        await ClinicDoctors.cancelInvitation(inv.id);
        this.invitations = this.invitations.filter(i => i.id !== inv.id);
        this.$toast.success("Convite cancelado");
      } catch (e) {
        this.$toast.error("Não foi possível cancelar o convite");
      } finally {
        this.cancelling = null;
        this.busyInviteId = null;
      }
    },
    formatDate(iso) {
      if (!iso) return "—";
      const [y, m, d] = iso.split("-");
      return `${d}/${m}/${y}`;
    },
    /**
     * Filtro mudou → grava na URL; o watch de $route.query refaz o fetch.
     * Busca por texto (q) é debounced — select de especialidade é imediato.
     */
    onFilter(key, value) {
      this.filters = { ...this.filters, [key]: value };
      if (key === "q") {
        clearTimeout(this._filterTimer);
        this._filterTimer = setTimeout(() => {
          this.clinicaSyncQuery({ [key]: value });
        }, 300);
        return;
      }
      this.clinicaSyncQuery({ [key]: value });
    },
    reload() {
      this.fetch();
    },
    occupationColor(occ) {
      if (occ >= 80) return "success";
      if (occ >= 60) return "primary";
      return "warning";
    },
    statusVariant(status) {
      return status === "ativo" ? "success" : "neutral";
    },
    goProfile(d) {
      this.$router.push({
        name: "ClinicDoctorProfile",
        params: { id: String(d.id) }
      });
    },
    goSchedule(d) {
      this.$router.push({
        name: "ClinicDoctorSchedule",
        params: { id: String(d.id) }
      });
    }
  },
  computed: {
    ...mapGetters("clinica", ["selectedUnitId"]),
    metrics() {
      const list = this.allDoctors;
      if (!list.length) return [];
      const active = list.filter(d => d.status === "ativo");
      const onboarding = list.filter(d => d.status === "onboarding").length;
      const totalAppts = list.reduce(
        (s, d) => s + (d.appointmentsMonth || 0),
        0
      );
      const avgOcc = active.length
        ? Math.round(
            active.reduce((s, d) => s + (d.occupation || 0), 0) / active.length
          )
        : 0;
      // Soma das Orders pagas no mês por médico (fonte canônica do backend).
      const billingCents = list.reduce((s, d) => s + (d.billingMonth || 0), 0);
      return [
        {
          label: "Profissionais de saúde ativos",
          value: String(active.length),
          foot: `${onboarding} em onboarding`
        },
        {
          label: "Consultas no mês",
          value: String(totalAppts),
          foot: `média ${
            active.length ? Math.round(totalAppts / active.length) : 0
          } por profissional de saúde`
        },
        {
          label: "Ocupação média",
          value: `${avgOcc}%`
        },
        {
          label: "Faturamento gerado",
          value: formatMoney(billingCents),
          valueClass: "money--pos",
          foot: "consultas pagas no mês"
        }
      ];
    }
  },
  watch: {
    selectedUnitId() {
      this.fetch();
    },
    "$route.query"() {
      this.filters = this.clinicaReadQuery({
        q: "",
        specialty: "",
        status: ""
      });
      this.fetch();
    }
  }
};
</script>
